import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/UseLanguage";
import { useNotification } from "src/contexts/NotificationContext";
import {
  markNotificationAsRead,
  fetchInAppNotificationsFull,
} from "src/services/NotificationAnnouncementsAPIs";
import { handleSubMenuClick } from "src/layouts/HomePage/Config/DashboardRoutes";
import { getApiLangFromI18n } from "src/utils/apiLang";
import type {
  AllNotificationsLocationState,
  InAppNotification,
} from "../Types/NotificationTypes";
import { getDashboardNameFromCategory } from "../Utils/NotificationCategoryIcons";
import {
  ALL_NOTIFICATIONS_PAGE_SIZE,
  FILTER_TO_DATE_RANGE,
  type NotificationListFilter,
} from "../Utils/NotificationConstants";
import { toPktDateString } from "../Utils/NotificationFilters";

export const useAllNotifications = () => {
  const { refreshCount, syncUnreadWithFetchedList } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  useLanguage();

  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [selectedFilter, setSelectedFilter] =
    useState<NotificationListFilter>("today");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  // Deep-link support: the bell popup navigates here with a target notification
  // id (+ its created_at) in router state. These refs make sure that jump only
  // ever happens once per arrival — after that, normal filter/page interactions
  // behave as usual.
  const targetNotificationId = (location.state as AllNotificationsLocationState | null)
    ?.targetNotificationId;
  const targetHandledRef = useRef(false);
  const skipNextPageResetRef = useRef(false);
  // Guards against a stale response (e.g. a slow earlier fetch) overwriting a
  // newer one — every fetch captures the sequence number current at its start
  // and only applies its result if nothing newer has started since.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    // Arriving via a bell-popup deep link: skip this default-state ("today",
    // page 1) fetch entirely rather than firing it and immediately discarding
    // the result — the deep-link effect below resolves the real filter/date/
    // page and this effect re-runs with that once it does.
    if (targetNotificationId && !targetHandledRef.current) {
      return;
    }

    const seq = ++requestSeqRef.current;
    setIsLoading(true);

    const params: Parameters<typeof fetchInAppNotificationsFull>[0] = {
      page: currentPage,
      page_size: ALL_NOTIFICATIONS_PAGE_SIZE,
      lang: getApiLangFromI18n(i18n),
    };
    if (selectedFilter === "dateRange") {
      if (dateRange.startDate) {
        params.start_date = dateRange.startDate;
      }
      if (dateRange.endDate) {
        params.end_date = dateRange.endDate;
      }
    } else {
      params.date_range = FILTER_TO_DATE_RANGE[selectedFilter];
    }

    (async () => {
      try {
        const data = await fetchInAppNotificationsFull(params);
        if (requestSeqRef.current !== seq) {
          return;
        }
        setNotifications(data.notifications);
        setTotalPages(data.pagination.totalPages);
        setError(null);
        // `unread_count` is the account-wide total, not just this page, so the
        // bell badge doesn't undercount based on whatever page happens to be showing.
        syncUnreadWithFetchedList(data.unread_count);
      } catch (err) {
        if (requestSeqRef.current !== seq) {
          return;
        }
        console.error("Error fetching notifications:", err);
        setNotifications([]);
        setError(
          t("allNotifications.errors.loadFailed", {
            defaultValue:
              "Something went wrong while loading this page. Please try again, or refresh the page.",
          })
        );
      } finally {
        if (requestSeqRef.current === seq) {
          setIsLoading(false);
        }
      }
    })();
  }, [
    selectedFilter,
    dateRange.startDate,
    dateRange.endDate,
    currentPage,
    i18n,
    syncUnreadWithFetchedList,
    targetNotificationId,
    t,
  ]);

  useEffect(() => {
    if (targetHandledRef.current || !targetNotificationId) {
      return;
    }
    targetHandledRef.current = true;

    const state = location.state as AllNotificationsLocationState | null;
    const createdAt = state?.createdAt;
    if (!createdAt) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const day = toPktDateString(createdAt);

    (async () => {
      try {
        const lookup = await fetchInAppNotificationsFull({
          start_date: day,
          end_date: day,
          lang: getApiLangFromI18n(i18n),
        });
        const index = lookup.notifications.findIndex(
          n => n.notification_id === targetNotificationId
        );
        if (index === -1) {
          return;
        }

        const page = Math.floor(index / ALL_NOTIFICATIONS_PAGE_SIZE) + 1;
        skipNextPageResetRef.current = true;
        setDateRange({ startDate: day, endDate: day });
        setSelectedFilter("dateRange");
        setCurrentPage(page);
        setHighlightedId(targetNotificationId);
      } catch (error) {
        console.error("Error resolving deep-linked notification:", error);
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
  }, [targetNotificationId, location, navigate, i18n]);

  useEffect(() => {
    if (skipNextPageResetRef.current) {
      skipNextPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [selectedFilter, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    if (!highlightedId) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      document
        .querySelector(`[data-notification-id="${highlightedId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const clearTimer = setTimeout(() => setHighlightedId(null), 3000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(clearTimer);
    };
  }, [highlightedId, notifications]);

  const filterOptions = [
    { value: "today", label: t("allNotifications.filters.today") },
    { value: "thisWeek", label: t("allNotifications.filters.thisWeek") },
    { value: "lastWeek", label: t("allNotifications.filters.lastWeek") },
    {
      value: "dateRange",
      label: t("allNotifications.filters.custom", { defaultValue: "Custom Range" }),
      disabled: true,
    },
  ];

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter as NotificationListFilter);
  };

  const handleDateRangeChange = ({ start, end }: { start?: string; end?: string }) => {
    setDateRange({ startDate: start || "", endDate: end || "" });
    setSelectedFilter("dateRange");
  };

  const handleNotificationClick = async (notification: InAppNotification) => {
    try {
      const success = await markNotificationAsRead(notification.notification_id);
      if (!success) {
        throw new Error(t("allNotifications.errors.markReadFailed"));
      }

      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notification.notification_id
            ? { ...n, is_read: true }
            : n
        )
      );

      await refreshCount();

      const dashboardName = getDashboardNameFromCategory(notification.category);
      handleSubMenuClick(dashboardName, navigate);
    } catch (error) {
      console.error("Error opening dashboard from notification:", error);
      message.error(t("allNotifications.errors.openDashboardFailed"));
    }
  };

  const goToPreviousPage = () => setCurrentPage(page => Math.max(1, page - 1));
  const goToNextPage = () =>
    setCurrentPage(page => Math.min(totalPages, page + 1));

  return {
    t,
    i18n,
    notifications,
    selectedFilter,
    isLoading,
    error,
    dateRange,
    currentPage,
    totalPages,
    highlightedId,
    filterOptions,
    handleFilterChange,
    handleDateRangeChange,
    handleNotificationClick,
    goToPreviousPage,
    goToNextPage,
  };
};
